import transformKeyCase from 'Utils/transformKeyCase';
import { camelize } from 'String';
import { type AnyObject } from '@Types';
import { type LessonProgress } from 'ContentProgress';
import { type Lesson, type LessonSnakeCasedAttrs } from './Lessons.types';

export function toSnakeCasedLesson<LessonContentLoaded extends boolean>(
    lesson: Lesson<LessonContentLoaded>,
): LessonSnakeCasedAttrs<LessonContentLoaded> {
    return transformKeyCase(lesson, { to: 'snakeCase' });
}

// Anything defined in a component with `this.key` that is camelized needs to be preserved.
const KEYS_COMPONENTS_DEFINE_CAMEL_CASED = [
    'overlayOptions',
    'hasBackground',
    'expectedText',
    'correctThreshold',
    'caseSensitive',
    'fontSize',
];

/*
    Same reconstruction as streamToIguanaAttrs: the Iguana localePackId getter reads locale_pack, and
    locale_pack_id is swallowed by a no-op setter (see is_content_item_mixin). LessonForDashboard
    carries only localePackId, and lesson-level locale pack ids key the prerequisite checks and the
    progress lookups.

    localePackId is read defensively because LessonForDashboard reaches lessonToIguanaAttrs through a
    cast, and the Lesson type does not describe that field.
*/
function rebuildLocalePack<FullLessonContentLoaded extends boolean>(
    lesson: Lesson<FullLessonContentLoaded>,
    snakeCased: LessonSnakeCasedAttrs<FullLessonContentLoaded>,
) {
    const localePackId = (lesson as { localePackId?: string }).localePackId;
    if (snakeCased.locale_pack || !localePackId) return;

    snakeCased.locale_pack = { id: localePackId } as LessonSnakeCasedAttrs<FullLessonContentLoaded>['locale_pack'];
}

export function lessonToIguanaAttrs<FullLessonContentLoaded extends boolean>(
    lesson: Lesson<FullLessonContentLoaded>,
    lessonProgress?: LessonProgress,
): LessonSnakeCasedAttrs<FullLessonContentLoaded> {
    const snakeCased = transformKeyCase<LessonSnakeCasedAttrs<FullLessonContentLoaded>>(
        { ...lesson, lessonProgress },
        {
            to: 'snakeCase',

            exclude: KEYS_COMPONENTS_DEFINE_CAMEL_CASED,
        },
    );

    rebuildLocalePack(lesson, snakeCased);

    const keyMap = {
        shared_content_for_image2_id: 'shared_content_for_image_2_id',
        context_image2_size: 'context_image_2_size',
        content_for_image2_id: 'content_for_image_2_id',
    } as const;

    if (!snakeCased.frames) return snakeCased;

    snakeCased.frames
        .flatMap(f => f?.components)
        .forEach(component => {
            if (!component) return;

            // Some components have keys like `shared_content_for_image_2_id` or `context_image_2_size`. When those are
            // camelized (coming down from the http api) and then re-snakecased (here), they end up being
            // `shared_content_for_image2_id`, etc. We need to change them back to what they originally were.
            Object.keys(component).forEach(key => {
                const newKey = keyMap[key as keyof typeof keyMap];
                if (newKey) {
                    const knownKey = key as (typeof keyMap)[keyof typeof keyMap];
                    component[newKey] = component[knownKey];
                    delete component[knownKey];
                }
            });

            // The old iguana code expects the keys of the behaviors object to be uppercase camelized, so
            // snake-casing the whole object messes them up.
            if (component.behaviors) {
                const origBehaviors = component.behaviors;
                component.behaviors = Object.keys(origBehaviors).reduce<Record<string, AnyObject>>((acc, name) => {
                    acc[camelize(name, true)] = origBehaviors[name];
                    return acc;
                }, {});
            }
        });

    return snakeCased;
}
